// ─── Imports ─────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";

// ─── Config ─────────────────────────────────────────────────────────────────
// Change this line to switch between local and production
//const IP = "http://localhost:8000"; // local
const IP = "https://chatbot.aux-rolplay.com/admin-api"; // production

// ─── API helpers ─────────────────────────────────────────────────────────────

// Basic Auth for /admin-api (nginx auth_basic). Stored in sessionStorage after first prompt.
function getAdminAuthHeader() {
    let basic = sessionStorage.getItem("chatbot_admin_basic");
    if (!basic) {
        const u = window.prompt("Usuario admin (chatbot)");
        const p = window.prompt("Password admin");
        if (!u || p === null) throw new Error("Autenticación cancelada");
        basic = btoa(`${u}:${p}`);
        sessionStorage.setItem("chatbot_admin_basic", basic);
    }
    return "Basic " + basic;
}

async function adminFetch(path, options = {}) {
    const headers = Object.assign({}, options.headers || {});
    headers["Authorization"] = getAdminAuthHeader();
    const res = await fetch(`${IP}${path}`, Object.assign({}, options, {
        credentials: "include",
        headers,
    }));
    if (res.status === 401) {
        sessionStorage.removeItem("chatbot_admin_basic");
        throw new Error("No autorizado — vuelve a cargar e inicia sesión admin");
    }
    return res;
}

const api = {
    list: () =>
        adminFetch("/clients").then((r) => r.json()),

    get: (id) =>
        adminFetch(`/clients/${id}`).then((r) => r.json()),

    create: (body) =>
        adminFetch("/clients", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        }).then(async (r) => {
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || "Error al crear");
            return data;
        }),

    update: (id, body) =>
        adminFetch(`/clients/${id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        }).then(async (r) => {
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || "Error al actualizar");
            return data;
        }),

    delete: (id) =>
        adminFetch(`/clients/${id}`, { method: "DELETE" }).then(async (r) => {
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || "Error al eliminar");
            return data;
        }),
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Try to pretty-print JSON, or return the raw string if it's already text */
function prettyJSON(val) {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    return JSON.stringify(val, null, 2);
}

/** Check if a string is valid JSON */
function isValidJSON(str) {
    if (!str.trim()) return true; // empty is allowed
    try { JSON.parse(str); return true; }
    catch { return false; }
}

// ─── Empty form state ─────────────────────────────────────────────────────────

const EMPTY_FORM = {
    client_id: "",
    name: "",
    prompt: "",
    knowledge: "",
    faqs: "",
};

// ─── Toast notification ───────────────────────────────────────────────────────

function Toast({ msg, type, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 3500);
        return () => clearTimeout(t);
    }, [onClose]);

    const colors = type === "error"
        ? "bg-red-600 text-white"
        : "bg-green-600 text-white";

    return (
        <div
            className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium
        flex items-center gap-3 ${colors}`}
        >
            <span>{msg}</span>
            <button onClick={onClose} className="opacity-70 hover:opacity-100">✕</button>
        </div>
    );
}

// ─── Delete confirmation modal ────────────────────────────────────────────────

function DeleteModal({ clientId, onConfirm, onCancel }) {
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
            <div className="bg-white border border-red-100 rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl">
                <h3 className="text-gray-900 font-bold text-lg mb-2">¿Eliminar cliente?</h3>
                <p className="text-gray-500 text-sm mb-6">
                    Esta acción eliminará permanentemente el perfil{" "}
                    <span className="text-gray-800 font-mono font-semibold">{clientId}</span> y toda su configuración.
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-600
              hover:bg-gray-50 transition text-sm font-medium"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700
              text-white transition text-sm font-semibold"
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── JSON field with validation indicator ─────────────────────────────────────

function JsonTextarea({ label, hint, value, onChange, rows = 12 }) {
    const valid = isValidJSON(value);

    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {label}
                </label>
                {value.trim() && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${valid
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-600"
                        }`}>
                        {valid ? "✓ JSON válido" : "✗ JSON inválido"}
                    </span>
                )}
            </div>
            {hint && <p className="text-gray-400 text-xs mb-2">{hint}</p>}
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                spellCheck={false}
                className={`w-full bg-gray-50 rounded-xl px-4 py-3 text-sm font-mono
          text-gray-800 border resize-y outline-none transition-colors
          placeholder:text-gray-300
          ${valid ? "border-gray-200 focus:border-red-400" : "border-red-400 focus:border-red-600"}`}
                placeholder={`Pega aquí el contenido del archivo...`}
            />
        </div>
    );
}

// ─── Main Admin Component ──────────────────────────────────────────────────────

export default function Admin() {
    const [clients, setClients] = useState([]);
    const [selected, setSelected] = useState(null); // client_id string
    const [form, setForm] = useState(EMPTY_FORM);
    const [isNew, setIsNew] = useState(false);
    const [loading, setLoading] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [toast, setToast] = useState(null); // { msg, type }
    const [deleteTarget, setDeleteTarget] = useState(null);

    // ── Toast helpers ────────────────────────────────────────────────────────

    const showToast = (msg, type = "success") => setToast({ msg, type });
    const closeToast = () => setToast(null);

    // ── Load client list ─────────────────────────────────────────────────────

    const loadList = useCallback(async () => {
        setListLoading(true);
        try {
            const data = await api.list();
            setClients(data);
        } catch {
            showToast("No se pudo conectar con la API", "error");
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => { loadList(); }, [loadList]);

    // ── Select a client ──────────────────────────────────────────────────────

    const handleSelect = async (clientId) => {
        setIsNew(false);
        setSelected(clientId);
        setLoading(true);
        try {
            const data = await api.get(clientId);
            setForm({
                client_id: data.client_id ?? "",
                name: data.name ?? "",
                prompt: data.prompt ?? "",
                knowledge: prettyJSON(data.knowledge),
                faqs: prettyJSON(data.faqs),
            });
        } catch {
            showToast("Error al cargar el perfil", "error");
        } finally {
            setLoading(false);
        }
    };

    // ── New client ───────────────────────────────────────────────────────────

    const handleNew = () => {
        setIsNew(true);
        setSelected(null);
        setForm(EMPTY_FORM);
    };

    // ── Field change ─────────────────────────────────────────────────────────

    const setField = (key) => (val) =>
        setForm((f) => ({ ...f, [key]: val }));

    // ── Validate form ────────────────────────────────────────────────────────

    const validate = () => {
        if (!form.client_id.trim()) return "El Client ID es obligatorio";
        if (!form.name.trim()) return "El nombre es obligatorio";
        if (!isValidJSON(form.knowledge)) return "El campo Knowledge no es JSON válido";
        if (!isValidJSON(form.faqs)) return "El campo FAQs no es JSON válido";
        return null;
    };

    // ── Save ─────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        const err = validate();
        if (err) { showToast(err, "error"); return; }

        setLoading(true);
        const body = {
            client_id: form.client_id.trim(),
            name: form.name.trim(),
            prompt: form.prompt,
            // Send as string so the API validator parses them (supports copy-paste JSON)
            knowledge: form.knowledge.trim() || null,
            faqs: form.faqs.trim() || null,
        };

        try {
            if (isNew) {
                await api.create(body);
                showToast(`Cliente "${body.client_id}" creado correctamente`);
                setIsNew(false);
                setSelected(body.client_id);
            } else {
                await api.update(selected, {
                    name: body.name,
                    prompt: body.prompt,
                    knowledge: body.knowledge,
                    faqs: body.faqs,
                });
                showToast("Cambios guardados");
            }
            await loadList();
        } catch (e) {
            showToast(e.message || "Error al guardar", "error");
        } finally {
            setLoading(false);
        }
    };

    // ── Delete ───────────────────────────────────────────────────────────────

    const handleDelete = async () => {
        setLoading(true);
        try {
            await api.delete(deleteTarget);
            showToast(`Cliente "${deleteTarget}" eliminado`);
            setDeleteTarget(null);
            setSelected(null);
            setForm(EMPTY_FORM);
            await loadList();
        } catch (e) {
            showToast(e.message || "Error al eliminar", "error");
        } finally {
            setLoading(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const hasSelection = isNew || selected;
    const jsonValid =
        isValidJSON(form.knowledge) && isValidJSON(form.faqs);

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col"
            style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>

            {/* ── Header ─────────────────────────────────────────────────────── */}
            <header className="border-b border-red-100 bg-white shadow-sm sticky top-0 z-30">
                <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center text-sm font-bold text-white">
                            C
                        </div>
                        <div>
                            <h1 className="text-sm font-bold text-gray-900 leading-none">Chatbot Admin</h1>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">{IP}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleNew}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600
              hover:bg-red-700 transition text-sm font-semibold text-white"
                    >
                        <span>+</span> Nuevo cliente
                    </button>
                </div>
            </header>

            <div className="flex flex-1 max-w-screen-xl mx-auto w-full px-6 py-6 gap-6">

                {/* ── Sidebar – client list ──────────────────────────────────────── */}
                <aside className="w-64 flex-shrink-0 flex flex-col gap-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1">
                        Clientes ({clients.length})
                    </p>

                    {listLoading && (
                        <div className="text-gray-400 text-sm text-center py-8 animate-pulse">
                            Cargando…
                        </div>
                    )}

                    {!listLoading && clients.length === 0 && (
                        <div className="text-gray-400 text-sm text-center py-8">
                            Sin clientes aún
                        </div>
                    )}

                    {clients.map((c) => (
                        <button
                            key={c.client_id}
                            onClick={() => handleSelect(c.client_id)}
                            className={`w-full text-left px-3.5 py-3 rounded-xl border transition
                ${selected === c.client_id && !isNew
                                    ? "bg-red-50 border-red-300 text-red-700"
                                    : "bg-white border-gray-200 text-gray-700 hover:bg-red-50 hover:border-red-200 hover:text-red-700"
                                }`}
                        >
                            <p className="text-sm font-semibold truncate">{c.name}</p>
                            <p className="text-xs font-mono text-gray-400 truncate mt-0.5">{c.client_id}</p>
                        </button>
                    ))}
                </aside>

                {/* ── Main – form ───────────────────────────────────────────────── */}
                <main className="flex-1 min-w-0">
                    {!hasSelection ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-300 gap-3 py-24">
                            <div className="text-5xl">🗂️</div>
                            <p className="text-sm">Selecciona un cliente o crea uno nuevo</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-5">

                            {/* ── Title row ── */}
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-bold text-gray-900">
                                    {isNew ? "Nuevo cliente" : `Editando — ${selected}`}
                                </h2>
                                {!isNew && (
                                    <button
                                        onClick={() => setDeleteTarget(selected)}
                                        className="text-red-500 hover:text-red-700 text-sm font-medium transition"
                                    >
                                        Eliminar cliente
                                    </button>
                                )}
                            </div>

                            {/* ── Identity fields ── */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Client ID
                                    </label>
                                    <input
                                        value={form.client_id}
                                        onChange={(e) => setField("client_id")(e.target.value)}
                                        disabled={!isNew}
                                        placeholder="salinasv2-somos"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5
                      text-sm font-mono text-gray-800 outline-none focus:border-red-400
                      disabled:opacity-40 disabled:cursor-not-allowed transition"
                                    />
                                    {isNew && (
                                        <p className="text-gray-400 text-xs mt-1">
                                            Identificador único, no se puede cambiar después
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Nombre
                                    </label>
                                    <input
                                        value={form.name}
                                        onChange={(e) => setField("name")(e.target.value)}
                                        placeholder="Salinas Somos"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5
                      text-sm text-gray-800 outline-none focus:border-red-400 transition"
                                    />
                                </div>
                            </div>

                            {/* ── Prompt ── */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Prompt del sistema
                                </label>
                                <p className="text-gray-400 text-xs mb-2">
                                    Copia y pega el contenido de tu archivo <span className="font-mono text-gray-500">.txt</span> tal cual
                                </p>
                                <textarea
                                    value={form.prompt}
                                    onChange={(e) => setField("prompt")(e.target.value)}
                                    rows={8}
                                    spellCheck={false}
                                    placeholder={"Eres un asistente especializado en...\n\nProblemas disponibles:\nproblema_1: ..."}
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3
                    text-sm font-mono text-gray-800 resize-y outline-none
                    focus:border-red-400 placeholder:text-gray-300 transition"
                                />
                            </div>

                            {/* ── Knowledge JSON ── */}
                            <JsonTextarea
                                label="Knowledge Base (JSON)"
                                hint={`Copia y pega el contenido de tu archivo knowledges/*.json`}
                                value={form.knowledge}
                                onChange={setField("knowledge")}
                                rows={14}
                            />

                            {/* ── FAQs JSON ── */}
                            <JsonTextarea
                                label="FAQs (JSON)"
                                hint={`Copia y pega el contenido de tu archivo FAQS/*.json`}
                                value={form.faqs}
                                onChange={setField("faqs")}
                                rows={10}
                            />

                            {/* ── Actions ── */}
                            <div className="flex items-center gap-3 pt-2 pb-8">
                                <button
                                    onClick={handleSave}
                                    disabled={loading || !jsonValid}
                                    className="px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700
                    disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold
                    text-white transition"
                                >
                                    {loading ? "Guardando…" : isNew ? "Crear cliente" : "Guardar cambios"}
                                </button>
                                {!isNew && (
                                    <button
                                        onClick={() => handleSelect(selected)}
                                        disabled={loading}
                                        className="px-6 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-100
                      text-sm font-medium text-gray-600 transition disabled:opacity-40"
                                    >
                                        Descartar cambios
                                    </button>
                                )}
                                {!jsonValid && (
                                    <p className="text-red-500 text-xs ml-1">
                                        Corrige el JSON inválido antes de guardar
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {/* ── Modals / Toasts ──────────────────────────────────────────────── */}

            {deleteTarget && (
                <DeleteModal
                    clientId={deleteTarget}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}

            {toast && (
                <Toast msg={toast.msg} type={toast.type} onClose={closeToast} />
            )}
        </div>
    );
}
